import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "./api";
import Search from "./Search";
import { getProfileBiodataSectionCatalog, generateProfileBiodataPdf } from "./profileBiodataPdf";
import { formatProfileNumberValue, getProfileNumberDisplay } from "./profileNumber";
import { formatTime12Hour } from "./timeValue";

const BRAND = {
  red: "#D73C2E",
  redDark: "#A53A24",
  text: "#1f2940",
  muted: "#6c7283",
  soft: "#fff6f3",
  softAlt: "#fffaf8",
  border: "rgba(215,60,46,0.16)",
  borderStrong: "rgba(215,60,46,0.24)",
  success: "#15825c",
  successSoft: "#ecfbf4",
  warning: "#b46a00",
  warningSoft: "#fff5e7",
  danger: "#bf2f2f",
  dangerSoft: "#fff0f0",
  shadow: "0 22px 60px rgba(185,52,58,0.12)",
};

const IMPORT_TEMPLATE_HEADERS = ["First Name", "Middle Name", "Last Name", "Gender", "DOB", "Birth Time"];
const EXPORT_SECTION_OPTIONS = [
  { id: "basic_details", label: "Basic Details" },
  { id: "about", label: "About" },
  { id: "contact_details", label: "Contact Details" },
  { id: "education", label: "Education" },
  { id: "career", label: "Career" },
  { id: "immediate_family", label: "Immediate Family" },
  { id: "extended_family", label: "Extended Family" },
  { id: "addresses", label: "Addresses" },
  { id: "partner_preferences", label: "Partner Preferences" },
];

export default function ImportExport({
  token,
  user = null,
  mode = "import",
  forcedBatchId = null,
  onBatchChange = null,
  onViewProfile = null,
}) {
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );
  const isExportMode = mode === "export";
  const isImportMode = !isExportMode;
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overview, setOverview] = useState({
    stats: { ready: 0, duplicate_review: 0, invalid: 0 },
    imports: [],
    exports: [],
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const csvInputRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchDetail, setBatchDetail] = useState({ batch: null, rows: [] });
  const [batchReloadToken, setBatchReloadToken] = useState(0);
  const [finalizingBatch, setFinalizingBatch] = useState(false);
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [lookups, setLookups] = useState({
    locations: [],
    cityTiers: [],
    castes: [],
    sakhas: [],
    educationCategories: [],
    visaStatuses: [],
    visaNames: [],
  });
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [selectedSections, setSelectedSections] = useState(
    EXPORT_SECTION_OPTIONS.reduce((acc, item) => ({ ...acc, [item.id]: true }), { profile_photo: true })
  );
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportSheetOpen, setExportSheetOpen] = useState(false);
  const [exportHistoryOpen, setExportHistoryOpen] = useState(false);

  useEffect(() => {
    if (forcedBatchId === null || forcedBatchId === undefined || forcedBatchId === "") return;
    setSelectedBatchId((current) => (String(current || "") === String(forcedBatchId) ? current : String(forcedBatchId)));
  }, [forcedBatchId]);

  useEffect(() => {
    onBatchChange?.(selectedBatchId);
  }, [onBatchChange, selectedBatchId]);

  useEffect(() => {
    if (!isImportMode && historyModalOpen) {
      setHistoryModalOpen(false);
    }
  }, [historyModalOpen, isImportMode]);

  useEffect(() => {
    if (!isExportMode) {
      setExportSheetOpen(false);
      setExportHistoryOpen(false);
    }
  }, [isExportMode]);

  useEffect(() => {
    if (!error && !success) return undefined;
    const timer = window.setTimeout(() => {
      setError("");
      setSuccess("");
    }, 2600);
    return () => window.clearTimeout(timer);
  }, [error, success]);

  const loadOverview = useCallback(async () => {
    try {
      setOverviewLoading(true);
      const response = await API.get("/import-export/overview", authHeaders);
      const nextOverview = {
        stats: response.data?.stats || { ready: 0, duplicate_review: 0, invalid: 0 },
        imports: Array.isArray(response.data?.imports) ? response.data.imports : [],
        exports: Array.isArray(response.data?.exports) ? response.data.exports : [],
      };
      setOverview(nextOverview);
      if (!selectedBatchId && nextOverview.imports[0]?.id) {
        setSelectedBatchId(nextOverview.imports[0].id);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load import export workspace.");
    } finally {
      setOverviewLoading(false);
    }
  }, [authHeaders, selectedBatchId]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!isExportMode || lookupsLoaded) return;
    let cancelled = false;
    Promise.allSettled([
      API.get("/locations"),
      API.get("/city-tiers"),
      API.get("/castes"),
      API.get("/sakhas"),
      API.get("/education-categories"),
      API.get("/visa-statuses"),
      API.get("/visa-names"),
    ]).then((results) => {
      if (cancelled) return;
      setLookups({
        locations: Array.isArray(results[0].value?.data) ? results[0].value.data : [],
        cityTiers: Array.isArray(results[1].value?.data) ? results[1].value.data : [],
        castes: Array.isArray(results[2].value?.data) ? results[2].value.data : [],
        sakhas: Array.isArray(results[3].value?.data) ? results[3].value.data : [],
        educationCategories: Array.isArray(results[4].value?.data) ? results[4].value.data : [],
        visaStatuses: Array.isArray(results[5].value?.data) ? results[5].value.data : [],
        visaNames: Array.isArray(results[6].value?.data) ? results[6].value.data : [],
      });
      setLookupsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isExportMode, lookupsLoaded]);

  useEffect(() => {
    if (!selectedBatchId) {
      setBatchDetail({ batch: null, rows: [] });
      return;
    }
    let cancelled = false;
    const loadBatch = async () => {
      try {
        setBatchLoading(true);
        const response = await API.get(`/import-export/imports/${selectedBatchId}`, authHeaders);
        if (cancelled) return;
        setBatchDetail({
          batch: response.data?.batch || null,
          rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
        });
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.error || "Failed to load import batch.");
      } finally {
        if (!cancelled) setBatchLoading(false);
      }
    };
    loadBatch();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, selectedBatchId, batchReloadToken]);

  const removeProfileSelection = useCallback((profileId) => {
    setSelectedProfiles((current) => current.filter((item) => String(item.id) !== String(profileId)));
  }, []);

  const toggleProfileSelection = useCallback((profile) => {
    if (!profile?.id) return;
    setSelectedProfiles((current) => (
      current.some((item) => String(item.id) === String(profile.id))
        ? current.filter((item) => String(item.id) !== String(profile.id))
        : current.concat(profile)
    ));
  }, []);

  const selectAllVisibleProfiles = useCallback((profiles = []) => {
    const visibleProfiles = (Array.isArray(profiles) ? profiles : []).filter((profile) => profile?.id);
    if (!visibleProfiles.length) return;
    setSelectedProfiles((current) => {
      const next = [...current];
      const existingIds = new Set(current.map((item) => String(item.id)));
      visibleProfiles.forEach((profile) => {
        if (!existingIds.has(String(profile.id))) {
          next.push(profile);
          existingIds.add(String(profile.id));
        }
      });
      return next;
    });
  }, []);

  const deselectAllVisibleProfiles = useCallback((profiles = []) => {
    const visibleIds = new Set((Array.isArray(profiles) ? profiles : []).map((profile) => String(profile?.id || "")).filter(Boolean));
    if (!visibleIds.size) return;
    setSelectedProfiles((current) => current.filter((item) => !visibleIds.has(String(item.id))));
  }, []);

  const toggleSection = useCallback((sectionId) => {
    setSelectedSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  }, []);

  const downloadTemplate = useCallback(() => {
    const csv = `${IMPORT_TEMPLATE_HEADERS.join(",")}\n`;
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "jkm_profile_import_template.csv");
  }, []);

  const submitCsvImport = useCallback(async () => {
    if (!csvFile) {
      setError("Select a CSV file.");
      return;
    }

    try {
      setImporting(true);
      setError("");
      setSuccess("");
      const formData = new FormData();
      formData.append("file", csvFile);
      const response = await API.post("/import-export/imports/csv", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setSuccess(response.data?.message || "CSV imported successfully.");
      setCsvFile(null);
      if (csvInputRef.current) {
        csvInputRef.current.value = "";
      }
      if (response.data?.batch_id) {
        setSelectedBatchId(response.data.batch_id);
      }
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to import CSV.");
    } finally {
      setImporting(false);
    }
  }, [csvFile, loadOverview, token]);

  const finalizeBatch = useCallback(async () => {
    if (!selectedBatchId) {
      setError("Select a batch to finalize.");
      return;
    }

    try {
      setFinalizingBatch(true);
      setError("");
      setSuccess("");
      const response = await API.post(`/import-export/imports/${selectedBatchId}/finalize`, {}, authHeaders);
      setSuccess(response.data?.message || "Batch finalized successfully.");
      await loadOverview();
      setBatchReloadToken((current) => current + 1);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to finalize batch.");
    } finally {
      setFinalizingBatch(false);
    }
  }, [authHeaders, loadOverview, selectedBatchId]);

  const deleteBatch = useCallback(async (batch) => {
    if (!batch?.id) return;
    if (!window.confirm(`Delete ${batch.original_file_name || `Batch #${batch.id}`}?`)) {
      return;
    }

    try {
      setDeletingBatchId(batch.id);
      setError("");
      setSuccess("");
      const response = await API.delete(`/import-export/imports/${batch.id}`, authHeaders);
      if (String(selectedBatchId) === String(batch.id)) {
        setSelectedBatchId(null);
        setBatchDetail({ batch: null, rows: [] });
        setHistoryModalOpen(false);
      }
      setSuccess(response.data?.message || "Import batch deleted.");
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete batch.");
    } finally {
      setDeletingBatchId(null);
    }
  }, [authHeaders, loadOverview, selectedBatchId]);

  const openBatch = useCallback((batch) => {
    if (!batch?.id) return;
    const nextBatchId = batch.id;
    const shouldOpenOverlay = isBatchFinalized(batch);

    setHistoryModalOpen(shouldOpenOverlay);

    if (String(selectedBatchId) === String(nextBatchId)) {
      setBatchLoading(true);
      setBatchReloadToken((current) => current + 1);
      return;
    }

    setBatchLoading(true);
    setBatchDetail({ batch: null, rows: [] });
    setSelectedBatchId(nextBatchId);
  }, [selectedBatchId]);

  const selectedSectionIds = useMemo(
    () => EXPORT_SECTION_OPTIONS.filter((item) => selectedSections[item.id]).map((item) => item.id),
    [selectedSections]
  );

  const loadSelectedProfileDetails = useCallback(async () => {
    const profileResponses = await Promise.all(
      selectedProfiles.map((profile) => API.get(`/profiles/${profile.id}`, authHeaders))
    );
    return profileResponses
      .map((response) => response.data)
      .filter(Boolean);
  }, [authHeaders, selectedProfiles]);

  const buildCsvExportRows = useCallback((profiles) => {
    const sectionHeaderOrder = [];
    const rows = profiles.map((profile) => {
      const sections = getProfileBiodataSectionCatalog(profile, {
        lookups,
        includePhone: true,
        includeEmail: true,
        includeAddresses: true,
        includeRelativePhone: true,
        includeRelativeEmail: true,
      }).filter((section) => selectedSectionIds.includes(section.id));

      const row = {
        "Profile Number": getProfileNumberDisplay(profile, ""),
        Name: [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" "),
      };

      if (selectedSections.profile_photo) {
        row["Primary Photo URL"] = profile.primary_photo_url_cache || "";
      }

      sections.forEach((section) => {
        if (section.type === "paragraph") {
          const header = section.title;
          row[header] = section.value || "";
          if (!sectionHeaderOrder.includes(header)) sectionHeaderOrder.push(header);
          return;
        }

        if (section.type === "list") {
          const header = section.title;
          row[header] = Array.isArray(section.items) ? section.items.join(" | ") : "";
          if (!sectionHeaderOrder.includes(header)) sectionHeaderOrder.push(header);
          return;
        }

        (Array.isArray(section.items) ? section.items : []).forEach((item) => {
          const header = `${section.title} - ${item.label}`;
          row[header] = item.value || "";
          if (!sectionHeaderOrder.includes(header)) sectionHeaderOrder.push(header);
        });
      });

      return row;
    });

    const headers = ["Profile Number", "Name"];
    if (selectedSections.profile_photo) headers.push("Primary Photo URL");
    headers.push(...sectionHeaderOrder);
    return { headers, rows };
  }, [lookups, selectedSectionIds, selectedSections.profile_photo]);

  const exportCsv = useCallback(async () => {
    if (!selectedProfiles.length) {
      setError("Select at least one profile.");
      return;
    }
    if (!selectedSectionIds.length && !selectedSections.profile_photo) {
      setError("Select at least one export field.");
      return;
    }

    try {
      setExportingCsv(true);
      setError("");
      setSuccess("");
      const profiles = await loadSelectedProfileDetails();
      const { headers, rows } = buildCsvExportRows(profiles);
      const csvText = [headers.join(",")]
        .concat(
          rows.map((row) =>
            headers
              .map((header) => toCsvCell(row[header]))
              .join(",")
          )
        )
        .join("\n");
      const fileName = `jkm_bulk_export_${Date.now()}.csv`;
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, fileName);
      await API.post("/import-export/exports/log", {
        format: "excel",
        profile_ids: selectedProfiles.map((profile) => profile.id),
        section_ids: selectedSectionIds,
        include_photo: selectedSections.profile_photo === true,
        row_count: rows.length,
        file_size_bytes: blob.size,
        file_name: fileName,
      }, authHeaders);
      setSuccess("CSV exported.");
      setSelectedProfiles([]);
      setExportSheetOpen(false);
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to export CSV.");
    } finally {
      setExportingCsv(false);
    }
  }, [authHeaders, buildCsvExportRows, loadOverview, loadSelectedProfileDetails, selectedProfiles, selectedSectionIds, selectedSections.profile_photo]);

  const exportPdf = useCallback(async () => {
    if (!selectedProfiles.length) {
      setError("Select at least one profile.");
      return;
    }
    if (!selectedSectionIds.length) {
      setError("Select at least one export section.");
      return;
    }

    try {
      setExportingPdf(true);
      setError("");
      setSuccess("");
      const profiles = await loadSelectedProfileDetails();
      let blob;
      let fileName;

      if (profiles.length === 1) {
        const generated = await generateProfileBiodataPdf(profiles[0], {
          lookups,
          includePhone: true,
          includeEmail: true,
          includeAddresses: true,
          includeRelativePhone: true,
          includeRelativeEmail: true,
          includePhoto: selectedSections.profile_photo === true,
          includedSectionIds: selectedSectionIds,
          output: "blob",
        });
        blob = generated.blob;
        fileName = generated.fileName || `${getProfileNumberDisplay(profiles[0]) || profiles[0].id}.pdf`;
      } else {
        const { default: JSZip } = await import("jszip");
        const zip = new JSZip();

        for (const profile of profiles) {
          const generated = await generateProfileBiodataPdf(profile, {
            lookups,
            includePhone: true,
            includeEmail: true,
            includeAddresses: true,
            includeRelativePhone: true,
            includeRelativeEmail: true,
            includePhoto: selectedSections.profile_photo === true,
            includedSectionIds: selectedSectionIds,
            output: "blob",
          });
          zip.file(generated.fileName || `${getProfileNumberDisplay(profile) || profile.id}.pdf`, generated.blob);
        }

        blob = await zip.generateAsync({ type: "blob" });
        fileName = `jkm_bulk_biodata_${Date.now()}.zip`;
      }

      downloadBlob(blob, fileName);
      await API.post("/import-export/exports/log", {
        format: "pdf",
        profile_ids: selectedProfiles.map((profile) => profile.id),
        section_ids: selectedSectionIds,
        include_photo: selectedSections.profile_photo === true,
        row_count: selectedProfiles.length,
        file_size_bytes: blob.size,
        file_name: fileName,
      }, authHeaders);
      setSuccess("PDF export prepared.");
      setSelectedProfiles([]);
      setExportSheetOpen(false);
      await loadOverview();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to export PDF.");
    } finally {
      setExportingPdf(false);
    }
  }, [authHeaders, loadOverview, loadSelectedProfileDetails, lookups, selectedProfiles, selectedSectionIds, selectedSections.profile_photo]);

  const importCounts = useMemo(() => {
    const rows = Array.isArray(batchDetail.rows) ? batchDetail.rows : [];
    return rows.reduce((summary, row) => {
      const status = String(row?.status || "").trim().toLowerCase();
      if (status === "ready") summary.ready += 1;
      else if (status === "duplicate_review") summary.duplicates += 1;
      else if (status === "invalid") summary.invalid += 1;
      return summary;
    }, { ready: 0, duplicates: 0, invalid: 0 });
  }, [batchDetail.rows]);

  const selectedBatchIsFinalized = isBatchFinalized(batchDetail.batch);

  return (
    <div>
      <style>{`
        @media (max-width: 980px) {
          .jk-import-grid2 {
            grid-template-columns: 1fr !important;
          }
          .jk-import-hero {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .jk-import-batch-meta {
            grid-template-columns: 1fr !important;
          }
          .jk-import-search-row {
            grid-template-columns: 1fr !important;
          }
          .jk-import-action-row {
            flex-direction: column !important;
          }
          .jk-import-history-row {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .jk-import-batch-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .jk-search-hero {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
        }
        @media (max-width: 720px) {
          .jk-import-search-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      {isImportMode && (
        <div style={S.hero} className="jk-import-hero">
          <div style={S.heroText}>
            <div style={S.eyebrow}>Data Operations</div>
            <h1 style={S.title}>CSV Import</h1>
            <p style={S.heroSub}>Upload profile CSV files, review staged rows, and finalize valid entries.</p>
          </div>
        </div>
      )}

      {isImportMode && (
        <>
          <div style={S.grid2} className="jk-import-grid2">
            <section style={S.card}>
              <div style={S.cardHeaderRow}>
                <h2 style={S.cardTitle}>Upload CSV</h2>
                <button type="button" style={S.secondaryBtn} onClick={downloadTemplate}>
                  Template
                </button>
              </div>
              <div style={S.inlineChips}>
                {IMPORT_TEMPLATE_HEADERS.map((label) => (
                  <span key={label} style={S.chip}>
                    {label}
                  </span>
                ))}
              </div>
              <div style={S.inputWrap}>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => setCsvFile(event.target.files?.[0] || null)}
                  style={S.fileInput}
                />
              </div>
              <button type="button" style={S.primaryBtn} onClick={submitCsvImport} disabled={importing}>
                {importing ? "Importing..." : "Import CSV"}
              </button>
            </section>

            <section style={S.card}>
              <h2 style={S.cardTitle}>Current Summary</h2>
              <div style={S.metricGrid}>
                <MetricCard label="Ready" value={overview.stats.ready} tone="success" />
                <MetricCard label="Duplicate Review" value={overview.stats.duplicate_review} tone="warning" />
                <MetricCard label="Invalid" value={overview.stats.invalid} tone="danger" />
              </div>
            </section>
          </div>

          <div style={S.grid2} className="jk-import-grid2">
            <section style={S.card}>
              <div style={S.cardHeaderRow}>
                <h2 style={S.cardTitle}>Recent Batches</h2>
                {overviewLoading && <span style={S.meta}>Loading...</span>}
              </div>
              <div style={S.listStack}>
                {overview.imports.map((item) => (
                  <div
                    key={item.id}
                    className="jk-import-batch-row"
                    style={String(selectedBatchId) === String(item.id) ? S.listItemActive : S.listItem}
                  >
                    <button
                      type="button"
                      onClick={() => openBatch(item)}
                      style={S.listItemBtn}
                    >
                      <div style={S.listTitle}>{item.original_file_name || `Batch #${item.id}`}</div>
                      <div style={S.listMeta}>
                        <span>{formatDateTime(item.created_at)}</span>
                        <span>{item.total_rows || 0} rows</span>
                        {item.finalized_at ? <span>Finalized</span> : <span>Pending Finalize</span>}
                      </div>
                    </button>
                    {!isBatchFinalized(item) && (
                      <button
                        type="button"
                        style={deletingBatchId === item.id ? S.deleteBtnDisabled : S.deleteBtn}
                        onClick={() => deleteBatch(item)}
                        disabled={deletingBatchId === item.id}
                      >
                        {deletingBatchId === item.id ? "Deleting..." : "Delete"}
                      </button>
                    )}
                  </div>
                ))}
                {!overview.imports.length && !overviewLoading && (
                  <div style={S.emptyPanel}>No import batches yet.</div>
                )}
              </div>
            </section>

            <section style={S.card}>
              <div style={S.cardHeaderRow}>
                <h2 style={S.cardTitle}>Batch Detail</h2>
                {batchLoading && <span style={S.meta}>Loading...</span>}
              </div>
              {batchDetail.batch ? (
                <ImportBatchPanel
                  batch={batchDetail.batch}
                  rows={batchDetail.rows}
                  counts={importCounts}
                  onFinalize={finalizeBatch}
                  finalizing={finalizingBatch}
                />
              ) : (
                <div style={S.emptyPanel}>Select a batch.</div>
              )}
            </section>
          </div>
        </>
      )}

      {isExportMode && (
        <>
          <Search
            user={user}
            onViewProfile={onViewProfile}
            selectionMode
            selectedProfileIds={selectedProfiles.map((profile) => String(profile.id))}
            onToggleProfileSelection={toggleProfileSelection}
            onSelectAllVisibleProfiles={selectAllVisibleProfiles}
            onDeselectAllVisibleProfiles={deselectAllVisibleProfiles}
            selectionCardMode="checkbox"
            heroEyebrow="Bulk Export Workspace"
            heroTitle="Find Profiles For Export"
            heroSubtitle="Apply filters, browse the same profile cards as search, and build your export queue."
            heroToolbarExtra={
              <button type="button" style={S.secondaryBtn} onClick={() => setExportHistoryOpen(true)}>
                History
              </button>
            }
            showPolicyNotice={false}
            showBaseStats={false}
            showAlertStats={false}
            showAssignedProfileStats={false}
            showAssignedTaskStats={false}
            initialSplashTitle="Profiles Ready For Export"
            initialSplashSubtitle="Use the same filters as Search to narrow profiles before selecting them for export."
            initialSplashActionLabel="Load Profiles"
            emptySplashTitle="No profiles found"
            emptySplashSubtitle="Adjust the selected filters and try again."
          />

          {selectedProfiles.length > 0 && (
            <button type="button" style={S.floatingExportBtn} onClick={() => setExportSheetOpen(true)}>
              Export {selectedProfiles.length} Profile{selectedProfiles.length === 1 ? "" : "s"}
            </button>
          )}
        </>
      )}

      {(error || success) && (
        <div
          style={{
            ...S.snackbar,
            ...(error ? S.snackbarError : S.snackbarSuccess),
          }}
        >
          {error || success}
        </div>
      )}

      {historyModalOpen && selectedBatchIsFinalized && (
        <div style={S.modalOverlay} onClick={() => setHistoryModalOpen(false)}>
          <div style={S.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={S.cardHeaderRow}>
              <div>
                <h2 style={S.cardTitle}>Batch History Detail</h2>
                <div style={S.meta}>Full batch detail with upload and finalize ownership.</div>
              </div>
              <button type="button" style={S.secondaryBtn} onClick={() => setHistoryModalOpen(false)}>
                Close
              </button>
            </div>

            {batchLoading ? (
              <div style={S.emptyPanel}>Loading batch detail...</div>
            ) : batchDetail.batch ? (
              <ImportBatchPanel
                batch={batchDetail.batch}
                rows={batchDetail.rows}
                counts={importCounts}
                onFinalize={finalizeBatch}
                finalizing={finalizingBatch}
              />
            ) : (
              <div style={S.emptyPanel}>Select a batch.</div>
            )}
          </div>
        </div>
      )}

      {exportSheetOpen && (
        <div style={S.modalOverlay} onClick={() => setExportSheetOpen(false)}>
          <div style={S.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={S.cardHeaderRow}>
              <div>
                <h2 style={S.cardTitle}>Export Selected Profiles</h2>
                <div style={S.meta}>
                  {selectedProfiles.length} profile{selectedProfiles.length === 1 ? "" : "s"} selected
                </div>
              </div>
              <button type="button" style={S.secondaryBtn} onClick={() => setExportSheetOpen(false)}>
                Close
              </button>
            </div>

            <div style={S.selectedWrap}>
              {selectedProfiles.map((profile) => (
                <div key={profile.id} style={S.selectedPill}>
                  <span>{formatProfileNo(profile)} - {profile.full_name}</span>
                  <button type="button" style={S.pillRemove} onClick={() => removeProfileSelection(profile.id)}>
                    x
                  </button>
                </div>
              ))}
            </div>

            <div style={S.exportSheetPanel}>
              <div style={S.cardHeaderRow}>
                <h3 style={S.cardTitle}>Export Fields</h3>
                <div style={S.listMeta}>
                  <span>{selectedProfiles.length} profiles</span>
                </div>
              </div>

              <div style={S.checkboxGrid}>
                <label style={S.checkboxCard}>
                  <input
                    type="checkbox"
                    checked={selectedSections.profile_photo === true}
                    onChange={() => toggleSection("profile_photo")}
                    style={S.checkboxInput}
                  />
                  <span style={S.checkboxLabelText}>Photo</span>
                </label>
                {EXPORT_SECTION_OPTIONS.map((item) => (
                  <label key={item.id} style={S.checkboxCard}>
                    <input
                      type="checkbox"
                      checked={selectedSections[item.id] === true}
                      onChange={() => toggleSection(item.id)}
                      style={S.checkboxInput}
                    />
                    <span style={S.checkboxLabelText}>{item.label}</span>
                  </label>
                ))}
              </div>

              <div style={S.actionRow} className="jk-import-action-row">
                <button type="button" style={S.primaryBtn} onClick={exportPdf} disabled={exportingPdf}>
                  {exportingPdf ? "Preparing PDF..." : "Export PDF"}
                </button>
                <button type="button" style={S.secondaryBtnStrong} onClick={exportCsv} disabled={exportingCsv}>
                  {exportingCsv ? "Preparing CSV..." : "Export CSV"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {exportHistoryOpen && (
        <div style={S.modalOverlay} onClick={() => setExportHistoryOpen(false)}>
          <div style={S.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={S.cardHeaderRow}>
              <div>
                <h2 style={S.cardTitle}>Recent Exports</h2>
                <div style={S.meta}>View recently prepared export files and their status.</div>
              </div>
              <button type="button" style={S.secondaryBtn} onClick={() => setExportHistoryOpen(false)}>
                Close
              </button>
            </div>

            <div style={S.listStack}>
              {overview.exports.map((item) => (
                <div key={item.id} style={S.historyRow} className="jk-import-history-row">
                  <div>
                    <div style={S.listTitle}>{String(item.export_type || "").toUpperCase()}</div>
                    <div style={S.listMeta}>
                      <span>{item.file_name || "-"}</span>
                      <span>{item.row_count || 0} rows</span>
                    </div>
                    <div style={S.listMeta}>
                      <span>Exported by {item.requested_by_actor?.name || "-"}</span>
                    </div>
                    <div style={S.listMeta}>
                      <span>
                        Profiles: {Array.isArray(item.profile_nos) && item.profile_nos.length
                          ? item.profile_nos.map((value) => formatProfileNo(value)).join(", ")
                          : "-"}
                      </span>
                    </div>
                  </div>
                  <div style={S.listMeta}>
                    <span>{formatDateTime(item.completed_at || item.created_at)}</span>
                    <span style={statusBadgeStyle(item.status)}>{humanizeStatus(item.status)}</span>
                  </div>
                </div>
              ))}
              {!overview.exports.length && !overviewLoading && (
                <div style={S.emptyPanel}>No exports yet.</div>
              )}
              {overviewLoading && !overview.exports.length && (
                <div style={S.emptyPanel}>Loading export history...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportBatchPanel({ batch, rows, counts, onFinalize, finalizing = false }) {
  const readyCount = Number(counts?.ready || 0);
  const canFinalize = !batch?.finalized_at && readyCount > 0 && typeof onFinalize === "function";

  return (
    <>
      <div style={S.metricGrid}>
        <MetricCard label="Ready" value={counts?.ready || 0} tone="success" compact />
        <MetricCard label="Duplicates" value={counts?.duplicates || 0} tone="warning" compact />
        <MetricCard label="Invalid" value={counts?.invalid || 0} tone="danger" compact />
      </div>

      <div style={S.batchMetaGrid} className="jk-import-batch-meta">
        <div style={S.batchMetaCard}>
          <div style={S.batchMetaLabel}>Batch Status</div>
          <div style={S.batchMetaValue}>
            <span style={statusBadgeStyle(batch?.status)}>{humanizeStatus(batch?.status)}</span>
          </div>
        </div>
        <div style={S.batchMetaCard}>
          <div style={S.batchMetaLabel}>Uploaded By</div>
          <div style={S.batchMetaValue}>{formatActor(batch?.uploaded_by_actor)}</div>
        </div>
        <div style={S.batchMetaCard}>
          <div style={S.batchMetaLabel}>Finalized By</div>
          <div style={S.batchMetaValue}>{batch?.finalized_at ? formatActor(batch?.finalized_by_actor) : "-"}</div>
        </div>
        <div style={S.batchMetaCard}>
          <div style={S.batchMetaLabel}>Finalized At</div>
          <div style={S.batchMetaValue}>{formatDateTime(batch?.finalized_at)}</div>
        </div>
      </div>

      <div style={S.finalizeRow}>
        <div style={S.finalizeHint}>
          {batch?.finalized_at
            ? `${batch?.finalized_profile_count || 0} profile${Number(batch?.finalized_profile_count || 0) === 1 ? "" : "s"} created and sent to verification.`
            : "Finalize creates profiles for all ready rows and sends them into the current verification flow."}
        </div>
        {!batch?.finalized_at && (
          <button type="button" style={canFinalize ? S.primaryBtn : S.primaryBtnDisabled} onClick={onFinalize} disabled={!canFinalize || finalizing}>
            {finalizing ? "Finalizing..." : "Finalize Batch"}
          </button>
        )}
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Row</th>
              <th style={S.th}>Name</th>
              <th style={S.th}>DOB</th>
              <th style={S.th}>Birth Time</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Match</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const normalized = row.normalized_data || {};
              const matches = Array.isArray(normalized.duplicate_matches) ? normalized.duplicate_matches : [];
              return (
                <tr key={row.id}>
                  <td style={S.td}>{row.row_no}</td>
                  <td style={S.td}>{normalized.full_name || buildName(normalized)}</td>
                  <td style={S.td}>{normalized.dob || "-"}</td>
                  <td style={S.td}>{trimSeconds(normalized.birth_time) || "-"}</td>
                  <td style={S.td}>
                    <span style={statusBadgeStyle(row.status)}>{humanizeStatus(row.status)}</span>
                  </td>
                  <td style={S.td}>{formatImportRowMatch(row, matches)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function MetricCard({ label, value, tone = "success", compact = false }) {
  const toneStyles = {
    success: { background: BRAND.successSoft, color: BRAND.success },
    warning: { background: BRAND.warningSoft, color: BRAND.warning },
    danger: { background: BRAND.dangerSoft, color: BRAND.danger },
  };

  return (
    <div
      style={{
        ...S.metricCard,
        ...(compact ? S.metricCardCompact : {}),
        background: toneStyles[tone]?.background || BRAND.softAlt,
      }}
    >
      <div style={S.metricLabel}>{label}</div>
      <div style={{ ...S.metricValue, color: toneStyles[tone]?.color || BRAND.text }}>{value}</div>
    </div>
  );
}

function toCsvCell(value) {
  const normalized = String(value ?? "");
  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

function buildName(row = {}) {
  return [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(" ");
}

function trimSeconds(value) {
  return formatTime12Hour(value);
}

function humanizeStatus(value) {
  return String(value || "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatActor(actor) {
  if (!actor) return "-";
  return actor.name || actor.email || (actor.id ? `User #${actor.id}` : "-");
}

function isBatchFinalized(batch) {
  return Boolean(batch?.finalized_at) || String(batch?.status || "").trim().toLowerCase() === "completed";
}

function formatProfileNo(value, gender) {
  if (value && typeof value === "object") {
    const displayValue = getProfileNumberDisplay(value, "");
    return displayValue ? `#${displayValue}` : "Profile";
  }
  if (value === null || value === undefined || value === "") return "Profile";
  return `#${formatProfileNumberValue(value, gender)}`;
}

function formatImportRowMatch(row, matches = []) {
  if (row?.created_profile_no) {
    return `${formatProfileNo(row.created_profile_no)} - ${row.created_profile_name || "Created"}`;
  }
  if (matches[0]) {
    return `${formatProfileNo(matches[0])} - ${matches[0].full_name || ""}`;
  }
  return "-";
}

function statusBadgeStyle(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "ready" || normalized === "completed") {
    return {
      ...S.statusBadge,
      color: BRAND.success,
      background: BRAND.successSoft,
      borderColor: "rgba(21,130,92,0.18)",
    };
  }
  if (normalized === "duplicate_review" || normalized === "review_required") {
    return {
      ...S.statusBadge,
      color: BRAND.warning,
      background: BRAND.warningSoft,
      borderColor: "rgba(180,106,0,0.16)",
    };
  }
  if (normalized === "invalid") {
    return {
      ...S.statusBadge,
      color: BRAND.danger,
      background: BRAND.dangerSoft,
      borderColor: "rgba(191,47,47,0.18)",
    };
  }
  return S.statusBadge;
}

const S = {
  hero: {
    background: "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(255,246,243,0.96))",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 34,
    padding: "30px 34px",
    boxShadow: BRAND.shadow,
    marginBottom: 22,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap",
  },
  heroText: {
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: BRAND.red,
    marginBottom: 10,
  },
  title: {
    margin: 0,
    fontSize: 46,
    lineHeight: 1,
    color: BRAND.text,
  },
  heroSub: {
    margin: "14px 0 0",
    color: BRAND.muted,
    fontSize: 15,
    lineHeight: 1.6,
    maxWidth: 720,
  },
  exportHeroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },
  exportWorkspace: {
    display: "grid",
    gap: 20,
  },
  exportHero: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 18,
    background: "linear-gradient(135deg, rgba(255,255,255,0.97), rgba(255,248,245,0.97))",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 30,
    padding: "28px 30px",
    boxShadow: BRAND.shadow,
  },
  exportEyebrow: {
    margin: "0 0 10px",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: BRAND.red,
  },
  exportTitle: {
    margin: 0,
    fontSize: 38,
    lineHeight: 1.05,
    color: BRAND.text,
  },
  exportSubtitle: {
    margin: "12px 0 0",
    color: BRAND.muted,
    fontSize: 15,
    lineHeight: 1.6,
    maxWidth: 680,
  },
  exportToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  exportToolbarGroup: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  exportToolbarLabel: {
    color: BRAND.muted,
    fontWeight: 800,
    fontSize: 13,
  },
  viewToggle: {
    display: "inline-flex",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: BRAND.softAlt,
    border: `1px solid ${BRAND.border}`,
  },
  viewToggleBtn: {
    border: "none",
    background: "transparent",
    color: BRAND.muted,
    fontWeight: 800,
    padding: "10px 14px",
    borderRadius: 999,
    cursor: "pointer",
  },
  viewToggleActive: {
    background: "#fff",
    color: BRAND.redDark,
    boxShadow: "0 8px 18px rgba(31,41,64,0.08)",
  },
  exportSelectionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    padding: "18px 22px",
    borderRadius: 24,
    background: "rgba(255,255,255,0.94)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 16px 34px rgba(185,52,58,0.08)",
  },
  exportSelectionLabel: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  exportSelectionValue: {
    color: BRAND.text,
    fontSize: 22,
    fontWeight: 900,
  },
  exportSelectionHint: {
    color: BRAND.muted,
    fontWeight: 700,
    fontSize: 14,
  },
  selectionGhostBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.redDark,
    fontWeight: 800,
    padding: "12px 16px",
    borderRadius: 16,
    cursor: "pointer",
  },
  searchWorkspaceCard: {
    background: "rgba(255,255,255,0.96)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 30,
    padding: 24,
    boxShadow: BRAND.shadow,
    display: "grid",
    gap: 18,
  },
  searchWorkspaceTitle: {
    margin: 0,
    color: BRAND.text,
    fontSize: 30,
  },
  searchWorkspaceMeta: {
    color: BRAND.muted,
    fontWeight: 800,
    fontSize: 14,
  },
  exportSearchBar: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: 12,
  },
  exportSearchInput: {
    width: "100%",
    padding: "18px 20px",
    borderRadius: 20,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontSize: 16,
    outline: "none",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
  },
  selectedRail: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  selectedTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 999,
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    color: BRAND.text,
    fontWeight: 800,
    cursor: "pointer",
  },
  selectedTagClose: {
    color: BRAND.redDark,
    fontSize: 14,
    fontWeight: 900,
  },
  exportEmptyState: {
    borderRadius: 24,
    border: `1px dashed ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    padding: "30px 24px",
    textAlign: "center",
  },
  exportEmptyTitle: {
    color: BRAND.text,
    fontSize: 22,
    fontWeight: 900,
    marginBottom: 8,
  },
  exportEmptySub: {
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  exportResultGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: 16,
  },
  exportResultList: {
    display: "grid",
    gap: 14,
  },
  exportProfileCard: {
    border: `1px solid ${BRAND.border}`,
    background: "#fff",
    borderRadius: 24,
    padding: 18,
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gap: 10,
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    boxShadow: "0 12px 28px rgba(31,41,64,0.06)",
  },
  exportProfileListCard: {
    border: `1px solid ${BRAND.border}`,
    background: "#fff",
    borderRadius: 24,
    padding: 18,
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gap: 10,
  },
  exportProfileCardSelected: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "linear-gradient(135deg, rgba(255,246,243,0.98), rgba(255,255,255,0.98))",
    boxShadow: "0 18px 34px rgba(215,60,46,0.12)",
  },
  exportProfileCardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  exportCardBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: BRAND.softAlt,
    border: `1px solid ${BRAND.border}`,
    color: BRAND.muted,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  exportCardBadgeSelected: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(215,60,46,0.10)",
    border: `1px solid ${BRAND.borderStrong}`,
    color: BRAND.redDark,
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  exportProfileNumber: {
    color: BRAND.redDark,
    fontWeight: 900,
    fontSize: 13,
  },
  exportProfileName: {
    color: BRAND.text,
    fontSize: 21,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  exportProfileMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    color: BRAND.muted,
    fontSize: 14,
    fontWeight: 700,
  },
  exportProfileSubMeta: {
    color: BRAND.muted,
    fontSize: 13,
    fontWeight: 700,
  },
  floatingExportBtn: {
    position: "fixed",
    right: 28,
    bottom: 26,
    zIndex: 40,
    border: "none",
    background: "linear-gradient(135deg, #1f9d63, #177c4d)",
    color: "#fff",
    fontWeight: 900,
    padding: "18px 22px",
    borderRadius: 999,
    cursor: "pointer",
    boxShadow: "0 26px 46px rgba(23,124,77,0.34)",
  },
  exportSheetPanel: {
    marginTop: 20,
  },
  alertError: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.dangerSoft,
    color: BRAND.danger,
    borderRadius: 24,
    padding: "14px 18px",
    fontWeight: 700,
    marginBottom: 18,
  },
  alertSuccess: {
    border: `1px solid rgba(21,130,92,0.18)`,
    background: BRAND.successSoft,
    color: BRAND.success,
    borderRadius: 24,
    padding: "14px 18px",
    fontWeight: 700,
    marginBottom: 18,
  },
  snackbar: {
    position: "fixed",
    left: "50%",
    bottom: 26,
    transform: "translateX(-50%)",
    zIndex: 80,
    minWidth: 280,
    maxWidth: "min(92vw, 560px)",
    padding: "14px 18px",
    borderRadius: 18,
    fontWeight: 800,
    boxShadow: "0 22px 50px rgba(31,41,64,0.18)",
    border: "1px solid transparent",
    textAlign: "center",
  },
  snackbarError: {
    background: BRAND.dangerSoft,
    color: BRAND.danger,
    borderColor: "rgba(191,47,47,0.18)",
  },
  snackbarSuccess: {
    background: BRAND.successSoft,
    color: BRAND.success,
    borderColor: "rgba(21,130,92,0.18)",
  },
  tabBar: {
    display: "inline-flex",
    gap: 10,
    padding: 8,
    borderRadius: 999,
    border: `1px solid ${BRAND.border}`,
    background: BRAND.softAlt,
  },
  tab: {
    border: "none",
    background: "transparent",
    color: BRAND.muted,
    fontWeight: 800,
    padding: "13px 20px",
    borderRadius: 999,
    cursor: "pointer",
  },
  tabActive: {
    border: "none",
    background: "linear-gradient(135deg, #ef4d37, #ce3629)",
    color: "#fff",
    fontWeight: 800,
    padding: "13px 20px",
    borderRadius: 999,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(215,60,46,0.22)",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 20,
    marginBottom: 20,
  },
  card: {
    background: "rgba(255,255,255,0.96)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 30,
    padding: 24,
    boxShadow: BRAND.shadow,
  },
  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },
  cardTitle: {
    margin: 0,
    fontSize: 30,
    color: BRAND.text,
  },
  inputWrap: {
    marginBottom: 16,
  },
  fileInput: {
    width: "100%",
    padding: "16px 18px",
    borderRadius: 20,
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    color: BRAND.text,
    fontSize: 16,
  },
  primaryBtn: {
    border: "none",
    background: "linear-gradient(135deg, #ef4d37, #ce3629)",
    color: "#fff",
    fontWeight: 800,
    padding: "16px 24px",
    borderRadius: 18,
    cursor: "pointer",
    boxShadow: "0 18px 28px rgba(215,60,46,0.22)",
  },
  primaryBtnDisabled: {
    border: "none",
    background: "linear-gradient(135deg, rgba(239,77,55,0.46), rgba(206,54,41,0.46))",
    color: "#fff",
    fontWeight: 800,
    padding: "16px 24px",
    borderRadius: 18,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  primaryBtnSlim: {
    border: "none",
    background: "linear-gradient(135deg, #ef4d37, #ce3629)",
    color: "#fff",
    fontWeight: 800,
    padding: "0 22px",
    borderRadius: 18,
    cursor: "pointer",
    minWidth: 120,
  },
  secondaryBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontWeight: 800,
    padding: "12px 18px",
    borderRadius: 16,
    cursor: "pointer",
  },
  secondaryBtnStrong: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    color: BRAND.redDark,
    fontWeight: 800,
    padding: "16px 24px",
    borderRadius: 18,
    cursor: "pointer",
  },
  inlineChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 18,
  },
  chip: {
    borderRadius: 999,
    padding: "10px 14px",
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    color: BRAND.text,
    fontWeight: 700,
    fontSize: 14,
  },
  choiceChip: {
    borderRadius: 999,
    padding: "12px 16px",
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontWeight: 800,
    cursor: "pointer",
  },
  choiceChipActive: {
    borderRadius: 999,
    padding: "12px 16px",
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.soft,
    color: BRAND.redDark,
    fontWeight: 800,
    cursor: "pointer",
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  checkboxCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 16px",
    borderRadius: 18,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    cursor: "pointer",
  },
  checkboxInput: {
    width: 18,
    height: 18,
    accentColor: BRAND.red,
    cursor: "pointer",
    flexShrink: 0,
  },
  checkboxLabelText: {
    color: BRAND.text,
    fontWeight: 800,
    fontSize: 15,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
  },
  metricCard: {
    borderRadius: 24,
    padding: "18px 18px 16px",
    border: "1px solid transparent",
  },
  metricCardCompact: {
    padding: "14px 14px 12px",
  },
  metricLabel: {
    color: BRAND.muted,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontSize: 12,
    marginBottom: 8,
  },
  metricValue: {
    fontSize: 34,
    fontWeight: 900,
    lineHeight: 1,
  },
  listStack: {
    display: "grid",
    gap: 12,
  },
  listItem: {
    border: `1px solid ${BRAND.border}`,
    background: "#fff",
    borderRadius: 20,
    padding: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
  },
  listItemActive: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    borderRadius: 20,
    padding: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    boxShadow: "inset 0 0 0 1px rgba(215,60,46,0.08)",
  },
  listItemBtn: {
    flex: 1,
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
  },
  listTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: BRAND.text,
    marginBottom: 6,
  },
  listMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    color: BRAND.muted,
    fontSize: 14,
  },
  emptyPanel: {
    borderRadius: 20,
    border: `1px dashed ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    padding: 18,
    color: BRAND.muted,
    fontWeight: 700,
  },
  batchMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginTop: 18,
  },
  batchMetaCard: {
    borderRadius: 18,
    border: `1px solid ${BRAND.border}`,
    background: BRAND.softAlt,
    padding: "14px 16px",
  },
  batchMetaLabel: {
    color: BRAND.muted,
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  batchMetaValue: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 700,
  },
  finalizeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    marginTop: 18,
  },
  finalizeHint: {
    flex: 1,
    minWidth: 220,
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 600,
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 20,
    border: `1px solid ${BRAND.border}`,
    marginTop: 18,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 700,
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    background: BRAND.softAlt,
    color: BRAND.muted,
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${BRAND.border}`,
  },
  td: {
    padding: "14px 16px",
    borderBottom: `1px solid ${BRAND.border}`,
    color: BRAND.text,
    fontSize: 14,
    verticalAlign: "top",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.softAlt,
    color: BRAND.text,
    fontWeight: 800,
    fontSize: 12,
  },
  meta: {
    color: BRAND.muted,
    fontWeight: 700,
    fontSize: 14,
  },
  searchRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: 12,
    marginBottom: 16,
  },
  input: {
    width: "100%",
    padding: "16px 18px",
    borderRadius: 18,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontSize: 16,
    outline: "none",
  },
  searchResultCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 20,
    padding: 16,
    background: "#fff",
  },
  selectedWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
  },
  selectedPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 999,
    background: BRAND.softAlt,
    border: `1px solid ${BRAND.border}`,
    color: BRAND.text,
    fontWeight: 800,
  },
  pillRemove: {
    border: "none",
    background: "transparent",
    color: BRAND.redDark,
    fontSize: 20,
    lineHeight: 1,
    cursor: "pointer",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 18,
  },
  historyRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 20,
    background: "#fff",
    padding: 16,
  },
  deleteBtn: {
    border: `1px solid rgba(191,47,47,0.22)`,
    background: BRAND.dangerSoft,
    color: BRAND.danger,
    fontWeight: 800,
    padding: "10px 14px",
    borderRadius: 14,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  deleteBtnDisabled: {
    border: `1px solid rgba(191,47,47,0.12)`,
    background: "rgba(255,240,240,0.7)",
    color: "rgba(191,47,47,0.6)",
    fontWeight: 800,
    padding: "10px 14px",
    borderRadius: 14,
    cursor: "not-allowed",
    whiteSpace: "nowrap",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(31,41,64,0.34)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 1000,
  },
  modalCard: {
    width: "min(1100px, 100%)",
    maxHeight: "88vh",
    overflowY: "auto",
    background: "rgba(255,255,255,0.98)",
    border: `1px solid ${BRAND.borderStrong}`,
    borderRadius: 30,
    padding: 24,
    boxShadow: BRAND.shadow,
  },
};
